import { queryClient } from "@/queryClient"
import { createOrder } from "@/services/orders/createOrder"
import { updateOrder } from "@/services/orders/updateOrder"
import { BASE_ORDERS_QUERY_KEY } from "@/services/queryKeys"
import { useProducts } from "@/services/products/listProducts"
import type { ConfigureOrderFormOutput } from "@/forms/configureOrderForm"

const {
  defaultServiceChargePercentage,
  defaultServiceChargeFlat,
  tenant,
} = useTenant()

export type CurrentOrderOrderItemModifier = {
  id: string
  groupId: string
  label: string
  priceOffset: number
}

export type CurrentOrderOrderItem = {
  product: {
    id: string
    hasVariablePrice: boolean
    name: string
    color: string
  } | null
  modifiers: CurrentOrderOrderItemModifier[] | null
  note: string | null
  price: number
  quantity: number
}

const getTotalModifierPriceOffset = (
  modifiers: CurrentOrderOrderItemModifier[]
) => modifiers.reduce((acc, v) => acc + v.priceOffset, 0)

type CurrentOrder = {
  id: string | null
  note: string | null
  serviceChargePercentage: number | null
  serviceChargeFlat: number | null
  createdAt: Date
  orderNumber: number | null
  orderItems: Array<CurrentOrderOrderItem>
  amountPaid: number
}

const currentOrder = ref<CurrentOrder>({
  id: null,
  note: null,
  serviceChargePercentage: tenant.isServiceChargeEnabled
    ? defaultServiceChargePercentage.value
    : 0,
  serviceChargeFlat: tenant.isServiceChargeEnabled
    ? defaultServiceChargeFlat.value
    : 0,
  createdAt: new Date(),
  orderNumber: null,
  orderItems: [],
  amountPaid: 0,
})

const setFromConfigureOrderForm = (
  formValues: ConfigureOrderFormOutput
) => {
  currentOrder.value.note = formValues.note || null
  currentOrder.value.serviceChargePercentage =
    formValues.serviceChargePercentage
  currentOrder.value.serviceChargeFlat =
    formValues.serviceChargeFlat
}

const getProductCount = (productId: string) => {
  return currentOrder.value.orderItems.reduce(
    (acc, item) =>
      item.product?.id === productId
        ? acc + item.quantity
        : acc,
    0
  )
}

const addItem = (newItem: CurrentOrderOrderItem) => {
  if (currentOrder.value.orderItems.length === 0) {
    currentOrder.value.serviceChargeFlat =
      tenant.isServiceChargeEnabled
        ? defaultServiceChargeFlat.value
        : 0
    currentOrder.value.serviceChargePercentage =
      tenant.isServiceChargeEnabled
        ? defaultServiceChargePercentage.value
        : 0
  }

  // need to update this.... needs to ignore price
  const existingItem = currentOrder.value.orderItems.find(
    (item) =>
      item.note === newItem.note &&
      item.product?.id == newItem.product?.id &&
      item.price === newItem.price &&
      ((newItem.modifiers &&
        item.modifiers &&
        item.modifiers.length ===
          newItem.modifiers.length &&
        item.modifiers.every((m) =>
          newItem.modifiers?.find((nm) => m.id === nm.id)
        )) ||
        !item.modifiers ||
        !newItem.modifiers)
  )

  if (existingItem) {
    existingItem.quantity += newItem.quantity
  } else {
    currentOrder.value.orderItems.push({
      ...newItem,
      price: Math.max(
        0,
        newItem.price +
          getTotalModifierPriceOffset(
            newItem.modifiers ?? []
          )
      ),
    })
  }
}

const incrementItemQuantity = (itemIndex: number) => {
  const existingItem =
    currentOrder.value.orderItems[itemIndex]
  if (!existingItem) return

  existingItem.quantity += 1
}

const removeItem = (itemIndex: number) => {
  currentOrder.value.orderItems.splice(itemIndex, 1)
}

const decrementItemQuantity = (itemIndex: number) => {
  const existingItem =
    currentOrder.value.orderItems[itemIndex]
  if (!existingItem) return

  if (existingItem.quantity <= 1) {
    removeItem(itemIndex)
  } else {
    existingItem.quantity -= 1
  }
}

const updateItem = (
  itemIndex: number,
  params: Partial<Omit<CurrentOrderOrderItem, "product">>
) => {
  currentOrder.value.orderItems[itemIndex] = {
    ...currentOrder.value.orderItems[itemIndex],
    ...params,
  }

  const currentItem =
    currentOrder.value.orderItems[itemIndex]
  if (
    currentItem.modifiers &&
    currentItem.modifiers.length > 0
  ) {
    const { data: products } = useProducts()

    const product = products.value?.find(
      (p) => p.id === currentItem.product?.id
    )
    if (!product) return

    currentItem.price = Math.max(
      0,
      product.price +
        getTotalModifierPriceOffset(currentItem.modifiers)
    )
  }
}

const clearCurrentOrder = () => {
  currentOrder.value = {
    id: null,
    note: null,
    serviceChargePercentage:
      defaultServiceChargePercentage.value,
    serviceChargeFlat: defaultServiceChargeFlat.value,
    createdAt: new Date(),
    orderNumber: null,
    orderItems: [],
    amountPaid: 0,
  }
}

const clearOrderItems = () => {
  currentOrder.value.orderItems = []
}

const totalOrderItemCostPence = computed(() =>
  currentOrder.value.orderItems.reduce(
    (accumulator, item) =>
      accumulator + item.price * item.quantity,
    0
  )
)

const totalQuantity = computed(() =>
  currentOrder.value.orderItems.reduce(
    (acc, item) => acc + item.quantity,
    0
  )
)

const saveCurrentOrder = async () => {
  queryClient.invalidateQueries({
    queryKey: [BASE_ORDERS_QUERY_KEY],
  })

  const response = currentOrder.value.id
    ? await updateOrder(currentOrder.value.id, {
        orderItems: currentOrder.value.orderItems.flatMap(
          (item) =>
            (
              new Array(item.quantity) as Array<{
                productId: string | null
                productModifierIds: string[] | null
                price: number | null
                note: string | null
              }>
            ).fill({
              productId: item.product?.id ?? null,
              productModifierIds:
                item.modifiers?.map((m) => m.id) ?? null,
              price: Math.max(0, item.price),
              note: item.note,
            })
        ),
        amountGratuity: 0,
        isOpen: false,
        isCollection: false,
        tableId: null,
        note: currentOrder.value.note,
        customerName: null,
        customerPhone: null,
        collectionAt: null,
        serviceChargePercentage:
          currentOrder.value.serviceChargePercentage,
        serviceChargeFlat:
          currentOrder.value.serviceChargeFlat,
      })
    : await createOrder({
        orderItems: currentOrder.value.orderItems.flatMap(
          (item) =>
            (
              new Array(item.quantity) as Array<{
                productId: string | null
                productModifierIds: string[] | null
                price: number | null
                note: string | null
              }>
            ).fill({
              productId: item.product?.id ?? null,
              productModifierIds:
                item.modifiers?.map((m) => m.id) ?? null,
              price: Math.max(0, item.price),
              note: item.note,
            })
        ),
        amountGratuity: 0,
        isOpen: false,
        isCollection: false,
        tableId: null,
        note: currentOrder.value.note,
        customerName: null,
        customerPhone: null,
        collectionAt: null,
        serviceChargePercentage:
          currentOrder.value.serviceChargePercentage,
        serviceChargeFlat:
          currentOrder.value.serviceChargeFlat,
      })

  currentOrder.value.id = response.id
  currentOrder.value.orderNumber = response.orderNumber
  return response
}

export const useCurrentOrder = () => ({
  currentOrder: readonly(currentOrder),
  addItem,
  getProductCount,
  totalOrderItemCostPence,
  totalQuantity,
  incrementItemQuantity,
  decrementItemQuantity,
  removeItem,
  updateItem,
  clearOrderItems,
  clearCurrentOrder,
  setFromConfigureOrderForm,
  saveCurrentOrder,
})
